import { motion } from "framer-motion";
import { MicroLabel } from "@/components/decor/CropMarks";

const FINISHES = [
  { name: "Matte Paper", tag: "Paper", accent: "#C0C0C0" },
  { name: "Foil", tag: "Finish", accent: "#B89B5E" },
  { name: "Emboss", tag: "Texture", accent: "#F5F1E8" },
  { name: "Spot UV", tag: "Finish", accent: "#0072CE" },
  { name: "Acrylic", tag: "Material", accent: "#FFD200" },
  { name: "Metal", tag: "Material", accent: "#C0C0C0" },
  { name: "Vinyl", tag: "Material", accent: "#D6007F" },
  { name: "Fabric", tag: "Material", accent: "#B89B5E" },
  { name: "Packaging Texture", tag: "Surface", accent: "#F5F1E8" },
  { name: "Premium Gifting", tag: "Finish", accent: "#B89B5E" },
];

const Swatch = ({ f, i }) => (
  <motion.div
    data-testid={`finish-swatch-${i}`}
    initial={{ opacity: 0, scale: 0.96 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.5, delay: (i % 5) * 0.05 }}
    className="relative group cursor-default"
    style={{
      aspectRatio: "1 / 1",
      background: "#0A0A0A",
      border: "1px solid rgba(245,241,232,0.18)",
      overflow: "hidden",
    }}
  >
    {/* texture lines */}
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden>
      {i % 5 === 0 && (
        <>
          {Array.from({ length: 24 }).map((_, k) => (
            <line key={k} x1="0" y1={k * 4 + 2} x2="100" y2={k * 4 + 2} stroke="rgba(245,241,232,0.06)" />
          ))}
        </>
      )}
      {i % 5 === 1 && (
        <>
          {Array.from({ length: 18 }).map((_, k) => (
            <line key={k} x1={k * 6} y1="0" x2={k * 6 + 30} y2="100" stroke={f.accent} strokeOpacity="0.12" />
          ))}
        </>
      )}
      {i % 5 === 2 && (
        <>
          <circle cx="50" cy="50" r="35" fill="none" stroke={f.accent} strokeOpacity="0.3" />
          <circle cx="50" cy="50" r="22" fill="none" stroke={f.accent} strokeOpacity="0.2" />
          <circle cx="50" cy="50" r="10" fill="none" stroke={f.accent} strokeOpacity="0.4" />
        </>
      )}
      {i % 5 === 3 && (
        <>
          {Array.from({ length: 36 }).map((_, k) => (
            <rect
              key={k}
              x={(k % 6) * 16 + 2}
              y={Math.floor(k / 6) * 16 + 2}
              width="6"
              height="6"
              fill={k % 2 === 0 ? f.accent : "rgba(245,241,232,0.2)"}
              opacity={k % 2 === 0 ? 0.18 : 0.5}
            />
          ))}
        </>
      )}
      {i % 5 === 4 && (
        <>
          <rect x="6" y="6" width="88" height="88" fill="none" stroke={f.accent} strokeOpacity="0.4" />
          <rect x="20" y="20" width="60" height="60" fill="none" stroke="rgba(245,241,232,0.18)" />
          <line x1="6" y1="6" x2="94" y2="94" stroke={f.accent} strokeOpacity="0.18" />
        </>
      )}
    </svg>

    <div className="absolute top-3 left-3 ko-overline" style={{ color: f.accent, fontSize: 9 }}>
      {f.tag}
    </div>
    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
      <h3
        style={{
          fontFamily: "Montserrat",
          fontWeight: 700,
          color: "#F5F1E8",
          fontSize: "clamp(14px, 1.2vw, 17px)",
          letterSpacing: "-0.005em",
        }}
      >
        {f.name}
      </h3>
      <span aria-hidden style={{ width: 8, height: 8, background: f.accent }} />
    </div>
  </motion.div>
);

const MaterialsCraftFinish = () => {
  return (
    <section
      data-testid="materials-craft-finish"
      className="relative"
      style={{ background: "#F5F1E8", paddingTop: 120, paddingBottom: 120 }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3">
              <span style={{ width: 22, height: 1, background: "#0A0A0A", opacity: 0.7 }} />
              <span className="ko-overline" style={{ color: "#0A0A0A" }}>
                07 / Craft
              </span>
            </div>
            <h2
              className="mt-6"
              style={{
                fontFamily: "Montserrat",
                fontWeight: 800,
                color: "#0A0A0A",
                fontSize: "clamp(34px, 5vw, 64px)",
                lineHeight: 0.98,
                letterSpacing: "-0.025em",
                textTransform: "uppercase",
                maxWidth: 900,
              }}
            >
              Materials. Finish.
              <br />
              <span style={{ color: "#B89B5E" }}>Production detail.</span>
            </h2>
          </div>
          <p
            style={{
              color: "#0A0A0A",
              maxWidth: 420,
              fontSize: 14,
              lineHeight: 1.7,
              opacity: 0.85,
            }}
          >
            Every print, product, and promotional item reflects care, finish, and
            professional detailing — the kind only a production-aware partner
            obsesses over.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px" style={{ background: "rgba(10,10,10,0.18)" }}>
          {FINISHES.map((f, i) => (
            <Swatch key={f.name} f={f} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default MaterialsCraftFinish;
